import { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import DOMPurify from "dompurify";
import EmojiPicker from "emoji-picker-react";
import { Bold, Italic, Underline, ImageIcon, Smile, Save, ListOrdered, ListIcon, Link } from "lucide-react";
import { htmlToProseMirrorJSON } from "./htmlToProseMirror";
import { FontSize } from "./tiptapextensions/FontSize";
import { FontFamily } from "./tiptapextensions/FontFamily";
import { FontHandler } from "./handlers/FontHandler";



export default function MyWordStyleEditor({
  value,
  onSave,
  isRTL = false,
}: {
  value: string | any;
  onSave: (safeHtml: string, json: any) => void;
  isRTL?: boolean;
}) {
  const [dirty, setDirty] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [fontSizeInput, setFontSizeInput] = useState("16");

  const editor = useEditor({
    editable: true,
    content: typeof value === "string" ? "" : value,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5] } }),
      BulletList,
      OrderedList,
      ListItem,
      Image.configure({ allowBase64: false }),
      TextStyle,
      Color,
      FontSize,
      FontFamily,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    onUpdate: () => setDirty(true),
  });

  useEffect(() => {
    if (!editor || !value) return;

    if (typeof value === "string") {
      try {
        const json = htmlToProseMirrorJSON(value);
        editor.commands.setContent(json, false as any);
      } catch {
        editor.commands.setContent(value, false as any);
      }
    } else {
      editor.commands.setContent(value, false as any);
    }

    setDirty(false);
  }, [value, editor]);

  if (!editor) return null;

  const handleSave = () => {
    const safeHtml = DOMPurify.sanitize(editor.getHTML(), { USE_PROFILES: { html: true } });
    setDirty(false);
    onSave(safeHtml, editor.getJSON());
  };

  const addImage = () => {
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const addEmoji = (emoji: any) => {
    editor.chain().focus().insertContent(emoji.emoji).run();
    setShowEmoji(false);
  };

  const setFontSize = (size: number) => {
    editor.chain().focus().setMark("fontSize", { fontSize: size }).run();
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setFontSizeInput(value);
    const num = parseInt(value);
    if (!isNaN(num)) setFontSize(num);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-lg relative">
      {/* TOOLBAR */}
      <div className="flex flex-wrap items-center gap-2 border-b p-2 bg-gray-50 sticky top-0 z-10 shadow-sm">
        <ToolbarButton active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><Underline size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()}><ListIcon size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></ToolbarButton>
        <FontHandler editor={editor} defaultFont="Cairo"/>
        <ToolbarButton onClick={addImage}><ImageIcon size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive("link")} onClick={() => { const url = window.prompt("Enter URL"); if (url) editor.chain().focus().setLink({ href: url }).run(); }}><Link size={16} /></ToolbarButton>

        {/* Font Size */}
        <div className="flex items-center gap-1">
          <select value={fontSizeInput} onChange={handleFontSizeChange} className="border rounded px-2 py-1 text-sm">
            {[12,14,16,18,20,24,28,32,36,48].map((size)=> <option key={size} value={size}>{size}px</option>)}
          </select>
          <input type="number" value={fontSizeInput} onChange={handleFontSizeChange} className="border rounded px-2 py-1 w-16 text-sm" placeholder="px" />
        </div>

        <ToolbarButton onClick={() => setShowEmoji((v) => !v)}><Smile size={16} /></ToolbarButton>
        {showEmoji && <div className="absolute z-50 mt-10"><EmojiPicker onEmojiClick={addEmoji} /></div>}

        <div className="ml-auto">
          <button type="button" disabled={!dirty} onClick={handleSave} className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm ${dirty?"bg-blue-600 text-white hover:bg-blue-700":"bg-gray-300 text-gray-600 cursor-not-allowed"}`}><Save size={16}/> Save</button>
        </div>
      </div>

      {/* EDITOR */}
      <div dir={isRTL ? "rtl" : "ltr"} className={`prose max-w-none text-justify ${isRTL ? "text-right prose-rtl" : "text-left"}`}>
        <EditorContent
          editor={editor}
          className="p-6 min-h-[400px] max-h-[80vh] overflow-y-auto focus:outline-none prose prose-slate dark:prose-invert w-full"
          style={{
            lineHeight: 1.6,
            fontFamily: isRTL
              ? "'Amiri', 'Cairo', serif"
              : "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          }}
        />
      </div>
    </div>
  );
}

function ToolbarButton({ children, onClick, active }: { children: React.ReactNode; onClick: () => void; active?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={`p-2 rounded ${active ? "bg-blue-600 text-white" : "bg-white hover:bg-gray-200"}`}>{children}</button>
  );
}